import type { DataPoint } from "~/tools/data-point";
import { differenceInMilliseconds } from "~/tools/date-time";
import { first } from "~/tools/first";
import { last } from "~/tools/last";
import { max } from "~/tools/max";
import { min } from "~/tools/min";
import { isNonEmptyArray } from "~/tools/non-empty-array";
import type { Point } from "~/tools/point";
import { prop } from "~/tools/prop";
import { round } from "~/tools/round";

const COORDINATES_PRECISION = 3;

interface ChartLayout {
	viewboxWidth: number;
	viewboxHeight: number;
	padding: number;
}

/**
 * Creates a converter that maps {@link DataPoint} values to SVG
 * {@link Point} coordinates within the given layout.
 *
 * Returns `undefined` when the data set is empty or has zero time duration.
 * The returned converter returns `undefined` for null-valued data points.
 */
export function toPoint(
	dataPoints: ReadonlyArray<DataPoint>,
	{ viewboxWidth, viewboxHeight, padding }: ChartLayout,
): undefined | ((dataPoint: DataPoint) => undefined | Point) {
	if (!isNonEmptyArray(dataPoints)) {
		return undefined;
	}

	const [startTime] = first(dataPoints);
	const [endTime] = last(dataPoints);

	const duration = differenceInMilliseconds(endTime, startTime);

	if (duration === 0) {
		return undefined;
	}

	const values = dataPoints.map(prop(1));
	const minValue = min(0, values);
	const maxValue = max(minValue, values);

	const amplitude = maxValue - minValue;
	const drawWidth = viewboxWidth - 2 * padding;
	const drawHeight = viewboxHeight - 2 * padding;

	const scaleX = drawWidth / duration;
	const scaleY = amplitude === 0 ? 0 : drawHeight / amplitude;

	return ([timestamp, value]: DataPoint): undefined | Point => {
		if (value == null) {
			return undefined;
		}

		const x = scaleX * differenceInMilliseconds(timestamp, startTime);
		const y = scaleY * (value - minValue);

		return {
			x: round(padding + x, COORDINATES_PRECISION),
			y: round(padding + drawHeight - y, COORDINATES_PRECISION),
		};
	};
}
